import { FactoryProvider, InjectionToken, ValueProvider } from '@angular/core';
import { SignalR } from '@core/model/framework/notifications/signalR';
import { TaggingMetricMapper } from '@adapters/tagging/taggingMetricMapper';
import { TaggingTracker } from '@core/model/domain/taggingTracker';
import { GetBankConnections } from '@core/usecase/getBankConnections';
import { GetBanks } from '@core/usecase/getBanks';
import { SaveWebauthBankConnection } from '@core/usecase/saveWebauthBankConnection';
import { WaitOnWebauthNotification } from '@core/usecase/waitOnWebauthNotification';
import { GetWebauthUrl } from '@core/usecase/getWebauthUrl';
import { WaitOnCaseStateNotification } from '@core/usecase/waitOnCaseStateNotification';
import { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { GetAngularDependenciesService } from '../app/getAngularDependencies.service';
import { Kernel } from './kernel';
import { SERVICE_ID } from './serviceId';

export const isSignalREnabledToken = new InjectionToken<boolean>('isSignalREnabledToken');
export const isTrackingEnabledToken = new InjectionToken<boolean>('isTrackingEnabledToken');
export const taggingTrackerToken = new InjectionToken<TaggingTracker>('taggingTrackerToken');
export const taggingMetricMapperToken = new InjectionToken<TaggingMetricMapper>(
  'Token : taggingMetricMapper - debug'
);
export const browserBroadcastToken = new InjectionToken<BrowserBroadcast>(
  'Token : browserBroadcastToken - debug'
);
export const loggerToken = new InjectionToken<BrowserBroadcast>('Token : loggerToken - debug');

export const coreDependenciesToAngularProviderMapper = (kernel: Kernel): ValueProvider[] => {
  return [
    {
      provide: SaveWebauthBankConnection,
      useValue: kernel.getByIdentifier(SaveWebauthBankConnection),
    },
    {
      provide: GetBankConnections,
      useValue: kernel.getByIdentifier(GetBankConnections),
    },
    {
      provide: WaitOnWebauthNotification,
      useValue: kernel.getByIdentifier(WaitOnWebauthNotification),
    },
    {
      provide: WaitOnCaseStateNotification,
      useValue: kernel.getByIdentifier(WaitOnCaseStateNotification),
    },
    {
      provide: GetBanks,
      useValue: kernel.getByIdentifier(GetBanks),
    },
    {
      provide: GetWebauthUrl,
      useValue: kernel.getByIdentifier(GetWebauthUrl),
    },
    {
      provide: SignalR,
      useValue: kernel.getByIdentifier(SERVICE_ID.signalR),
    },
    {
      provide: isSignalREnabledToken,
      useValue: kernel.getByIdentifier(SERVICE_ID.signalRFeatureFlag),
    },
    {
      provide: isTrackingEnabledToken,
      useValue: kernel.getByIdentifier(SERVICE_ID.isTrackingEnabled),
    },
    {
      provide: browserBroadcastToken,
      useValue: kernel.getByIdentifier(SERVICE_ID.browserBroadcast),
    },
    {
      provide: loggerToken,
      useValue: kernel.getByIdentifier(SERVICE_ID.logger),
    },
  ];
};

export const bindAngularDependenciesToCoreAndMapToAngularProvider = (kernel: Kernel): FactoryProvider[] => {
  return [
    {
      provide: taggingTrackerToken,
      useFactory: (getAngularDependenciesService: GetAngularDependenciesService) => {
        if (kernel.isBound(SERVICE_ID.taggingTracker)) {
          throw new Error(
            `${SERVICE_ID.taggingTracker} already bound ! This function should not be called twice`
          );
        }

        const tagCommanderService = getAngularDependenciesService.getTagCommanderService();
        return kernel.bindTaggingTracker(tagCommanderService);
      },
      deps: [GetAngularDependenciesService],
    },
    {
      provide: taggingMetricMapperToken,
      useFactory: (getAngularDependenciesService: GetAngularDependenciesService) => {
        if (kernel.isBound(SERVICE_ID.taggingMetricMapper)) {
          throw new Error(
            `${SERVICE_ID.taggingMetricMapper} already bound ! This function should not be called twice`
          );
        }

        const tagCommanderMetricMapper = getAngularDependenciesService.getTagCommanderMetricMapper();
        return kernel.bindTaggingMetricMapper(tagCommanderMetricMapper);
      },
      deps: [GetAngularDependenciesService],
    },
  ];
};
